/**
 * Подавление «шумных» ошибок внешних источников (Яндекс.Карты, некоторые
 * браузерные расширения), которые не влияют на работу приложения, но засоряют
 * консоль и ловятся глобальными обработчиками.
 *
 * Что улучшено по сравнению с исходной версией:
 *  - идемпотентность: плагин безопасно выполняется повторно (HMR/перезапуск),
 *    не оборачивая нативные функции несколько раз;
 *  - единый предикат shouldIgnore вместо разбросанных проверок;
 *  - каждый участок изолирован в try/catch и не роняет инициализацию.
 *
 * ВАЖНО: это осознанный workaround, а не норма. Он маскирует реальные ошибки,
 * если они случайно совпадут с паттернами ниже, поэтому список паттернов
 * держим минимальным.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const shouldIgnore = (msg: string) =>
    !!msg && (msg.includes('startTime') || msg.includes('reportAllChanges'))

  // Идемпотентность: повторное выполнение (например, при HMR) ничего не дублирует.
  const guard = globalThis as any
  if (guard.__arthExternalErrorGuard) return
  guard.__arthExternalErrorGuard = true

  // 1) Performance API: расширения могут вернуть записи с нечисловым startTime,
  //    из-за чего сторонние библиотеки (React DevTools и т.п.) кидают исключения.
  try {
    const proto: any = Performance && (Performance as any).prototype
    for (const k of ['getEntries', 'getEntriesByType', 'getEntriesByName'] as const) {
      const orig = proto?.[k]
      if (typeof orig !== 'function' || (orig as any).__arthPatched) continue
      const wrapped = function (...args: any[]) {
        try {
          const res = orig.apply(this, args)
          if (!Array.isArray(res)) return res
          return res
            .map((e: any) => {
              if (e && typeof e.startTime !== 'number') { try { e.startTime = 0 } catch {} }
              return e
            })
            .filter(Boolean)
        } catch { return [] }
      }
      ;(wrapped as any).__arthPatched = true
      proto[k] = wrapped
    }
  } catch {}

  // 2) requestIdleCallback: страховка от некорректного deadline.
  const origRIC = window.requestIdleCallback
  if (origRIC && !(origRIC as any).__arthPatched) {
    const wrappedRIC = ((cb: IdleRequestCallback, opts?: IdleRequestOptions) => {
      return origRIC((deadline: any) => {
        try {
          if (!deadline || typeof deadline.timeRemaining !== 'function') {
            deadline = { didTimeout: false, timeRemaining: () => 0, startTime: 0 } as any
          }
          cb(deadline)
        } catch {}
      }, opts)
    }) as typeof origRIC
    ;(wrappedRIC as any).__arthPatched = true
    window.requestIdleCallback = wrappedRIC
  }

  // 3) window.onerror (только игнорируемые паттерны).
  const origOnError = window.onerror
  window.onerror = function (msg: any, src, line, col, err) {
    const m = String(msg ?? '') + ' ' + String((err as any)?.message ?? '')
    if (shouldIgnore(m)) return true
    return origOnError ? (origOnError as any).call(window, msg, src, line, col, err) : false
  }

  // 4) Ошибки ресурсов/скриптов (capture-фаза) + Яндекс.Карты.
  const errorHandler = (e: ErrorEvent) => {
    const m = String(e.message ?? '') + ' ' + String((e.error as any)?.message ?? '')
    if (shouldIgnore(m) || String(e.filename ?? '').includes('api-maps.yandex')) {
      e.preventDefault()
      e.stopImmediatePropagation()
    }
  }
  window.addEventListener('error', errorHandler, true)

  // 5) Необработанные отклонённые промисы.
  const rejectionHandler = (e: any) => {
    const reason = e?.reason
    const m = String((reason && reason.message) || reason || '')
    if (shouldIgnore(m)) e.preventDefault()
  }
  window.addEventListener('unhandledrejection', rejectionHandler)

  // 6) console.error.
  try {
    const origCE = console.error
    console.error = (...a: any[]) => {
      if (shouldIgnore(a.map(String).join(' '))) return
      return (origCE as any).apply(console, a)
    }
  } catch {}

  // 7) Глобальный обработчик Vue.
  try {
    nuxtApp.vueApp.config.errorHandler = (err: any, _inst, _info) => {
      if (shouldIgnore(String(err?.message ?? err ?? ''))) return
      console.error(err)
    }
  } catch {}
})
